import { computed, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { environment } from '@dxp/core/environment';
import {
    DefaultTelXLUser,
    DefaultUser,
    IAgentClientState,
    IRbacUserTenant,
    IUser,
    NBU_ID,
} from '@dxp/core/models';
import {
    signalStore,
    withComputed,
    withHooks,
    withMethods,
    withState,
} from '@ngrx/signals';
import {
    catchError,
    combineLatest,
    delay,
    distinctUntilChanged,
    EMPTY,
    exhaustMap,
    filter,
    forkJoin,
    interval,
    map,
    of,
    switchMap,
    tap,
    timer,
} from 'rxjs';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import {
    DirectoryApiService,
    PresenceApiService,
    RbacApiService,
} from '@dxp/core/api';
import { updateState } from '@angular-architects/ngrx-toolkit';
import { AGENT_SIGNALR_HUB } from '@dxp/core/signalr';
import { calculateUserPresence } from '@dxp/core/helpers';
import { HubService, LoggerService, ReloadService } from '@dxp/core/services';
import { hasAuthenticatedSub } from './user.helpers';

interface IUserStoreState {
    loggedIn: IUser;
    state: IAgentClientState;
    loggingOut: boolean;
    canChangeState: boolean;
    authenticated: boolean;
}

const initialState: IUserStoreState = {
    loggedIn: DefaultTelXLUser,
    state: {} as IAgentClientState,
    loggingOut: false,
    canChangeState: true,
    authenticated: false,
};

export const UserStore = signalStore(
    { providedIn: 'root' },
    environment.storeWithDevTools('UserStore'),
    withState<IUserStoreState>(initialState),
    withMethods(() => {
        const tick = signal(0);

        return {
            incrementTick() {
                tick.update(v => v + 1);
            },

            get tick() {
                return tick;
            },
        };
    }),
    withComputed(store => ({
        ready: computed<boolean>(() => store.loggedIn().authenticated),
        user: computed<IUser>(() => {
            store.tick();

            return {
                ...store.loggedIn(),
                presence:
                    Object.keys(store.state()).length > 0
                        ? calculateUserPresence(
                              store.state().agentProperties.state,
                              store.state().agentProperties.stateSince,
                              store.state().agentProperties.breakName,
                              store.state().agentProperties.timedBreakExpiresAt,
                          )
                        : calculateUserPresence('LoggedOut', new Date(), ''),
                canChangeState: store.canChangeState(),
            };
        }),
        hideSensitiveAgentInformation: computed<boolean>(() => {
            if (Object.keys(store.state()).length === 0) return true;

            const { level, displaySettings } = store.state().agentProperties;
            return level === 'Supervisor'
                ? false
                : (displaySettings.hideSensitiveAgentInformation ?? true);
        }),
        allowAgentsToSummariseInHistory: computed<boolean>(() => {
            if (Object.keys(store.state()).length === 0) return false;

            const { level, displaySettings } = store.state().agentProperties;
            return level === 'Supervisor'
                ? true
                : (displaySettings.allowAgentsToSummariseInHistory ?? false);
        }),
        allowAgentsToSummariseDuringWrapUp: computed<boolean>(() => {
            if (Object.keys(store.state()).length === 0) return false;

            const { level, displaySettings } = store.state().agentProperties;
            return level === 'Supervisor'
                ? true
                : (displaySettings.allowAgentsToSummariseDuringWrapUp ?? false);
        }),
    })),
    withMethods(store => ({
        updateUser(user: IUser) {
            updateState(store, 'UPDATE_USER', state => {
                return {
                    ...state,
                    loggedIn: {
                        ...state.loggedIn,
                        ...user,
                    },
                };
            });
        },
        updateUserState(userState: IAgentClientState) {
            updateState(store, 'UPDATE_USER_STATE', state => {
                return {
                    ...state,
                    state: userState,
                };
            });
        },
        updateUserAuthenticated(isAuthenticated: boolean) {
            updateState(store, 'UPDATE_USER_AUTHENTICATED', state => {
                return {
                    ...state,
                    authenticated: isAuthenticated,
                };
            });
        },
        updateCanChangeState(canChangeState: boolean) {
            updateState(store, 'UPDATE_USER_CAN_CHANGE_STATE', state => {
                return {
                    ...state,
                    canChangeState: canChangeState,
                };
            });
        },
        endSession() {
            updateState(store, 'UPDATE_USER_LOGOUT', state => {
                return {
                    ...state,
                    loggingOut: true,
                };
            });
        },
    })),
    withMethods(
        (
            store,
            presenceApiService = inject(PresenceApiService),
            destroyRef = inject(DestroyRef),
        ) => ({
            activate() {
                if (!store.canChangeState()) {
                    return;
                }

                store.updateCanChangeState(false);

                presenceApiService
                    .activate()
                    .pipe(
                        switchMap(() => timer(2000)),
                        tap(() => store.updateCanChangeState(true)),
                        takeUntilDestroyed(destroyRef),
                    )
                    .subscribe({
                        error: () => {
                            store.updateCanChangeState(true);
                        },
                    });
            },
            gotoBreak(breakName: string) {
                if (!store.canChangeState()) {
                    return;
                }

                store.updateCanChangeState(false);

                presenceApiService
                    .goToBreak(breakName)
                    .pipe(
                        switchMap(() => timer(2000)),
                        tap(() => store.updateCanChangeState(true)),
                        takeUntilDestroyed(destroyRef),
                    )
                    .subscribe({
                        error: () => {
                            store.updateCanChangeState(true);
                        },
                    });
            },
            logout() {
                presenceApiService
                    .logout()
                    .pipe(takeUntilDestroyed(destroyRef))
                    .subscribe();
            },
        }),
    ),
    withHooks({
        onInit(
            store,
            agentHubService = inject(AGENT_SIGNALR_HUB),
            oidcSecurityService = inject(OidcSecurityService),
            rbacApiService = inject(RbacApiService),
            presenceApiService = inject(PresenceApiService),
            directoryApiService = inject(DirectoryApiService),
            hubService = inject(HubService),
            loggerService = inject(LoggerService),
            reloadService = inject(ReloadService),
            destroyRef = inject(DestroyRef),
        ) {
            combineLatest(
                oidcSecurityService.checkAuth(),
                toObservable(reloadService.refreshTime),
            )
                .pipe(
                    tap(([auth]) => {
                        store.updateUserAuthenticated(auth.isAuthenticated);

                        loggerService.info(
                            `Authentication initialization the user is ${
                                auth.isAuthenticated ? '' : 'not '
                            }authenticated.`,
                        );

                        if (auth.isAuthenticated) {
                            loggerService.debug(
                                'User information',
                                auth.userData,
                            );
                            loggerService.debug(
                                'Access token',
                                auth.accessToken,
                            );
                        }
                    }),
                    map(([auth, refresh]) => ({
                        auth: auth,
                        refresh,
                    })),
                    filter(x => hasAuthenticatedSub(x.auth)),
                    distinctUntilChanged(
                        (a, b) =>
                            a.auth.userData.userId === b.auth.userData.userId &&
                            a.refresh === b.refresh,
                    ),
                    map(x => x.auth.userData.sub),
                    switchMap(userId =>
                        presenceApiService.login().pipe(
                            catchError(() => EMPTY),
                            map(() => userId),
                        ),
                    ),
                    switchMap(userId =>
                        forkJoin({
                            tenants: rbacApiService
                                .getConnectedUsers(userId)
                                .pipe(
                                    catchError(error => {
                                        reloadService.error(
                                            'USER_STORE',
                                            error.message,
                                        );

                                        return of([] as IRbacUserTenant[]);
                                    }),
                                ),
                            user: rbacApiService.getUser(userId).pipe(
                                catchError(error => {
                                    reloadService.error(
                                        'USER_STORE',
                                        error.message,
                                    );

                                    return of(DefaultUser(userId));
                                }),
                            ),
                            photo: directoryApiService.getPhoto().pipe(
                                catchError(() => {
                                    return of(null);
                                }),
                            ),
                            businessUnits: rbacApiService
                                .getAgentAttributes(userId)
                                .pipe(
                                    map(attributes => [
                                        ...attributes
                                            .filter(
                                                a =>
                                                    a.type === 'BusinessUnit' &&
                                                    a.enabled,
                                            )
                                            .map(a => a.key),
                                        NBU_ID,
                                    ]),
                                    catchError(error => {
                                        reloadService.error(
                                            'USER_STORE',
                                            error.message,
                                        );

                                        return of([NBU_ID]);
                                    }),
                                ),
                        }),
                    ),
                    tap(({ tenants, user, photo, businessUnits }) => {
                        const userUpdate: IUser = {
                            ...user,
                            tenants,
                            businessUnits,
                            photo: photo
                                ? URL.createObjectURL(photo)
                                : undefined,
                            authenticated: true,
                        } as IUser;

                        store.updateUser(userUpdate);
                    }),
                    takeUntilDestroyed(destroyRef),
                )
                .subscribe();

            agentHubService.agentState$
                .pipe(
                    tap((userState: IAgentClientState) => {
                        store.updateUserState(userState);
                    }),
                    takeUntilDestroyed(destroyRef),
                )
                .subscribe();

            agentHubService.agentState$
                .pipe(
                    map((s: IAgentClientState) => s.agentProperties.state),
                    filter(state => state === 'LoggedOut'),
                    distinctUntilChanged(),
                    delay(1000),
                    tap(() => hubService.stopSignalRHubs()),
                    exhaustMap(() =>
                        oidcSecurityService.logoffAndRevokeTokens().pipe(
                            tap(() => {
                                localStorage.clear();
                                sessionStorage.clear();
                            }),
                        ),
                    ),

                    takeUntilDestroyed(destroyRef),
                )
                .subscribe();

            interval(1000)
                .pipe(takeUntilDestroyed(destroyRef))
                .subscribe(() => store.incrementTick());
        },
    }),
);
